import React from "react";
import { Mail, Youtube, X, Globe2 } from "lucide-react";

export default function Portfolio() {
  return (
    <div className="min-h-screen relative text-gray-100 antialiased">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/images/portfolio_bg.jpg')"
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-800/95" />
      
      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto py-16 px-6">
        {/* HERO */}
        <header className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#60a5fa] drop-shadow-lg">
            Suman Chakraborty
          </h1>
          <p className="mt-3 text-lg md:text-xl text-gray-100 drop-shadow-md">
            Bringing Products and Brands Forward Through Storytelling
          </p>
          <p className="mt-2 text-sm text-gray-300 drop-shadow-sm">
            Crafting content that connects people to products, through stories that entertain, inform, and inspire action.
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-3 items-center text-sm">
            <a
              href="https://www.youtube.com/@TheShinobiClan"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
              target="_blank"
              rel="noreferrer"
            >
              <Youtube size={16} />
              Shinobi Talks Tech
            </a>

            <a
              href="https://x.com/a_fresh_shinobi"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
              target="_blank"
              rel="noreferrer"
            >
              <X size={16} />
              @a_fresh_shinobi
            </a>

            <a
              href="https://techpp.com/author/suman/"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
              target="_blank"
              rel="noreferrer"
            >
              <Globe2 size={16} />
              TechPP Articles
            </a>

            <a
              href="mailto:suman.rahul46@gmail.com"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
            >
              <Mail size={16} />
              suman.rahul46@gmail.com
            </a>
          </div>
        </header>

        {/* ABOUT */}
        <section className="bg-gray-900/60 backdrop-blur-md rounded-lg shadow-2xl p-6 mb-8 border border-gray-600/40">
          <h2 className="text-xl font-semibold text-[#60a5fa] mb-2 drop-shadow-md">About Me</h2>
          <p className="text-gray-200 leading-relaxed drop-shadow-sm">
            Hey, I'm <strong className="text-gray-100">Suman</strong>, a content professional with a knack for storytelling that bridges brands and audiences. From writing long-form blogs to crafting social media narratives and producing engaging videos, I create stories that move people, literally and emotionally. Over the years I've built a strong presence on YouTube through <em>Shinobi Talks Tech</em> and on X as <em>@a_fresh_shinobi</em>.
            where I explore tech, creativity, and culture with my audience.
          </p>
        </section>

        {/* CASE STUDIES - cards */}
        <section className="grid gap-6">
          {/* Card 1 - Enhanced result highlighting */}
          <article className="bg-gray-900/60 backdrop-blur-md rounded-lg shadow-2xl p-6 border border-gray-600/40">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0">
              <div className="flex-1">
                <h3 className="text-lg font-bold">
                  <a
                    href="https://youtube.com/shorts/gofsUwAONO4?feature=share"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#60a5fa] hover:underline drop-shadow-md"
                  >
                    Style Meets Protection with Ringke Cases
                  </a>
                </h3>
                <div className="mt-2 text-sm text-gray-300 flex flex-wrap gap-2">
                  <span><span className="text-[#60a5fa] font-medium">Brand:</span> Ringke India</span>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <span><span className="text-[#60a5fa] font-medium">Platform:</span> YouTube: Shinobi Talks Tech</span>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <span><span className="text-[#60a5fa] font-medium">Content Type:</span> Short-form product video</span>
                </div>
              </div>
              <div className="bg-green-900/30 border border-green-500/40 rounded-full px-3 py-1 text-sm text-green-300 font-semibold shadow-lg backdrop-blur-sm sm:flex-shrink-0">
                📈 10K+ views
              </div>
            </div>

            <hr className="my-4 border-gray-600/50" />
            <p className="text-gray-200 leading-relaxed">
              Ringke wanted to highlight how their phone cases protect without hiding the device's original design. I created a quick, engaging short showing the case in use, demonstrating drop protection while emphasizing the clear, sleek finish.
            </p>
            <p className="mt-3 text-gray-200">
              The storytelling focused on protection with personality, showing how users can display their phone's aesthetics while keeping it safe.
            </p>
          </article>

          {/* Card 2 - Enhanced result highlighting */}
          <article className="bg-gray-900/60 backdrop-blur-md rounded-lg shadow-2xl p-6 border border-gray-600/40">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0">
              <div className="flex-1">
                <h3 className="text-lg font-bold">
                  <a
                    href="https://x.com/a_fresh_shinobi/status/1966105966887534963"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#60a5fa] hover:underline drop-shadow-md"
                  >
                    A Clutch on a Budget: Kreo
                  </a>
                </h3>
                <div className="mt-2 text-sm text-gray-300 flex flex-wrap gap-2">
                  <span><span className="text-[#60a5fa] font-medium">Brand:</span> Kreo (India)</span>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <span><span className="text-[#60a5fa] font-medium">Platform:</span> YouTube: Shinobi Talks Tech</span>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <span><span className="text-[#60a5fa] font-medium">Content Type:</span> Short-form review</span>
                </div>
              </div>
              <div className="bg-blue-900/30 border border-blue-500/40 rounded-full px-3 py-1 text-sm text-blue-300 font-semibold shadow-lg backdrop-blur-sm sm:flex-shrink-0">
                🚀 20K+ views, brand-promoted
              </div>
            </div>

            <hr className="my-4 border-gray-600/50" />
            <p className="text-gray-200 leading-relaxed">
              Kreo, an Indian gaming peripheral brand, launched a lightweight RGB gaming mouse that rivaled expensive global brands at just a quarter of the price. My video focused on value-driven performance, real gameplay, quick response tests, and how Kreo is redefining accessible gaming gear.
            </p>
            <p className="mt-3 text-gray-200">
              The tone balanced energy and authenticity, speaking directly to gamers tired of overpriced imports.
            </p>
          </article>

          {/* Card 3 - Enhanced result highlighting */}
          <article className="bg-gray-900/60 backdrop-blur-md rounded-lg shadow-2xl p-6 border border-gray-600/40">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0">
              <div className="flex-1">
                <h3 className="text-lg font-bold">
                  <a
                    href="https://x.com/a_fresh_shinobi/status/1966105966887534963"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#60a5fa] hover:underline drop-shadow-md"
                  >
                    Turning Credit Into Connection: Super Money App
                  </a>
                </h3>
                <div className="mt-2 text-sm text-gray-300 flex flex-wrap gap-2">
                  <span><span className="text-[#60a5fa] font-medium">Brand:</span> Super Money</span>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <span><span className="text-[#60a5fa] font-medium">Platform:</span> X: @a_fresh_shinobi</span>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <span><span className="text-[#60a5fa] font-medium">Content Type:</span> Social media campaign</span>
                </div>
              </div>
              <div className="bg-purple-900/30 border border-purple-500/40 rounded-full px-3 py-1 text-sm text-purple-300 font-semibold shadow-lg backdrop-blur-sm sm:flex-shrink-0">
                🔥 Viral, strong sign-ups
              </div>
            </div>

            <hr className="my-4 border-gray-600/50" />
            <p className="text-gray-200 leading-relaxed">
              Super Money needed to drive sign-ups for its credit card rewards app. I created a concise, curiosity-driven post combining a relatable money insight with the offer. Instead of using a hard-sell CTA, I wrote it as an authentic personal tip, positioning the card as a smart move rather than a promotion.
            </p>
            <p className="mt-3 text-gray-200">
              The post went viral within fintech circles, generating thousands of impressions and a notable spike in app sign-ups in under 48 hours.
            </p>
          </article>
        </section>

        {/* FOOTER */}
        <footer className="mt-10 text-center text-sm text-gray-300">
          <div className="mt-5 flex flex-wrap justify-center gap-3 items-center text-sm">
            <a
              href="https://www.youtube.com/@TheShinobiClan"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
              target="_blank"
              rel="noreferrer"
            >
              <Youtube size={16} />
              Shinobi Talks Tech
            </a>

            <a
              href="https://x.com/a_fresh_shinobi"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
              target="_blank"
              rel="noreferrer"
            >
              <X size={16} />
              @a_fresh_shinobi
            </a>

            <a
              href="mailto:suman.rahul46@gmail.com"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md bg-gray-900/70 backdrop-blur-sm shadow-lg border border-gray-600/50 hover:bg-gray-800/80 hover:border-gray-500/70 transition-all duration-300"
            >
              <Mail size={16} />
              suman.rahul46@gmail.com
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
